import { COOKIE_EXPERIMENTAL_FEATURES } from "constants/auth_cookies"
import { useMutation } from "@tanstack/react-query"
import { getCookie } from "@tokenterminal/core/utils/cookies/get-cookie"
import { useAuth } from "context/AuthContext"
import { apiPOST } from "data/api/post"
import { useEffect, useState } from "react"

const getCookieValue = () => {
  const cookie = getCookie(COOKIE_EXPERIMENTAL_FEATURES)

  try {
    if (!cookie) {
      return null
    }

    return JSON.parse(decodeURIComponent(cookie))
  } catch (e) {
    return null
  }
}

export const EXPERIMENTAL_FLAGS = {
  REDESIGNED_HOMEPAGE: "redesigned_homepage",
} as const

export function useExperimentalFlags(): [
  Record<string, boolean>,
  (flagUpdate: Record<string, boolean>) => Promise<{ success: boolean }>,
] {
  const { user } = useAuth()
  const [flags, setFlags] = useState(getCookieValue())
  const allowedFlags = Object.values(EXPERIMENTAL_FLAGS)

  const { mutateAsync: setExperimentalFlags } = useMutation({
    mutationFn: (flagUpdate: Record<string, boolean>) => {
      const filteredFlagUpdate = Object.fromEntries(
        Object.entries(user.feature_flags).filter(([key]) =>
          allowedFlags.includes(
            key as (typeof EXPERIMENTAL_FLAGS)[keyof typeof EXPERIMENTAL_FLAGS]
          )
        )
      )

      return apiPOST(`/v1/users/me/feature-flags`, {
        feature_flags: {
          ...filteredFlagUpdate,
          ...flagUpdate,
        },
      })
    },
  })

  useEffect(() => {
    if (user) {
      setFlags(getCookieValue())
    }
  }, [user])

  return [flags ?? {}, setExperimentalFlags]
}

export function useExperimentalFlagEnabled(
  featureKey: (typeof EXPERIMENTAL_FLAGS)[keyof typeof EXPERIMENTAL_FLAGS],
  fallback = false
): { enabled: boolean; loading: boolean } {
  const { user } = useAuth()
  const [cookie, setCookie] = useState(getCookieValue())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      setCookie(getCookieValue())
    }
    setLoading(false)
  }, [user])

  return {
    enabled: cookie?.[featureKey] ?? fallback,
    loading,
  }
}
